import Icon from 'components/atoms/Icon';
import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import ProjectJobRoleSelectFormField from 'components/molecules/SelectFormField/ProjectJobRoleSelectFormField/ProjectJobRoleSelectFormField';
import {
  ProjectJobRoleFindManyQuery,
  ReportRenderType,
  ReportSubType,
  ReportType,
  useReportRenderFindManyLazyQuery,
} from 'generated/graphql';
import { usePollingReportRenderQuery } from 'helpers/reportRender';
import { formatTitleCase } from 'helpers/strings';
import { TSelectFormFieldValue } from 'interfaces/forms/form';
import { useRef, useState } from 'react';
import ReportSubTypeFormField from 'views/ModalView/ReportsModal/ReportSubTypeFormField/ReportSubTypeFormField';
import {
  Button,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ReportsManagement.module.scss';

const ExistingReport = ({
  projectJobRoles,
  stageCandidateId,
  isMultimatch,
  isDisabled,
  selectedJobRoleId,
  setSelectedJobRoleId,
}: {
  projectJobRoles?: ProjectJobRoleFindManyQuery;
  stageCandidateId: number;
  isMultimatch: boolean;
  isDisabled: boolean;
  selectedJobRoleId: TSelectFormFieldValue;
  setSelectedJobRoleId: (value: TSelectFormFieldValue) => void;
}) => {
  const { handleMsgType } = useNotification();
  const reportRenderSubIdRef = useRef<string | null>('existing');
  const [reportSubType, setReportSubType] = useState<TSelectFormFieldValue>('');

  const { startPolling, isPolling, reportRenderQueryResponse } =
    usePollingReportRenderQuery(reportRenderSubIdRef.current, true);

  const [reportRenderFindManyQuery] = useReportRenderFindManyLazyQuery({
    onError: error => {
      handleMsgType({ type: TNotification.error, message: error?.message });
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      if (data?.report) {
        reportRenderSubIdRef.current = data.report[0].subId;
        startPolling();
      }
    },
    variables: {
      subType: reportSubType as ReportSubType,
      dataSourceId: Number(stageCandidateId),
      reportRenderType: ReportRenderType.Pdf,
      projectJobRoleId: Number(selectedJobRoleId),
    },
  });

  const handleReportSubType = (value: TSelectFormFieldValue) => {
    setReportSubType(value);
  };

  const handleSelectedJobRoleId = (value: TSelectFormFieldValue) => {
    setSelectedJobRoleId(value);
  };

  const handleGetReport = () => {
    reportRenderFindManyQuery();
  };

  const handleOpenGeneratedReport = () => {
    if (reportRenderQueryResponse.data?.ReportRender?.blob?.url) {
      window.open(
        `${reportRenderQueryResponse.data?.ReportRender?.blob?.url}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <div>
      <p>Access existing report</p>
      <div className={styles.reportNameContainer}>
        <ReportSubTypeFormField
          id="subTypeFormField"
          name="subType"
          label="Sub type"
          value={reportSubType}
          onChange={handleReportSubType}
          selectedReportType={ReportType.StageResults}
          isDisabled={isDisabled}
        />
        {isMultimatch && (
          <ProjectJobRoleSelectFormField
            id="projectJobRoleSelect"
            name="projectJobRole"
            label="Role"
            projectJobRoles={projectJobRoles?.ProjectJobRoleFindMany ?? []}
            onChange={handleSelectedJobRoleId}
          />
        )}
        <Button
          variant="primary"
          disabled={isDisabled || !reportSubType || !selectedJobRoleId}
          loading={isPolling}
          onClick={handleGetReport}
        >
          Get report
        </Button>
      </div>
      {reportRenderQueryResponse?.data?.ReportRender && (
        <div className={styles.generatedReportInfo}>
          <InfoItem
            label="Report subId"
            value={reportRenderQueryResponse?.data?.ReportRender?.subId}
            hasCopyButton
          />
          <InfoItem
            label="Status"
            value={formatTitleCase(
              reportRenderQueryResponse?.data?.ReportRender?.reportRenderStatus?.toString() ||
                ''
            )}
          />
          <div className={styles.openTabButtonContainer}>
            <Button
              variant="secondary"
              size="small"
              disabled={isDisabled}
              leftIcon={<Icon icon="openTab" />}
              onClick={handleOpenGeneratedReport}
            >
              Open report in new tab
            </Button>
          </div>
        </div>
      )}
      {isDisabled && (
        <p className={styles.disabledWarning}>
          * Reports are not available until the candidate has completed the
          assessment stage.
        </p>
      )}
    </div>
  );
};

export default ExistingReport;
